import type { ScoreEntry } from '@cardengine/shared';

/**
 * The cabinets' side of the high-score board.
 *
 * The ported games were written against a local array of `{ name, score, wave }`
 * and still are — this maps that shape onto the server's records rather than
 * making them learn a new one, and Stack, which was written here, was written
 * to it for the same reason. `wave` is the one figure all four happen to keep,
 * under two names on screen: a wave in Last Line and Shard, a level in Circuit
 * and Stack.
 */
export interface BoardEntry {
  name: string;
  score: number;
  wave: number;
}

/** What a cabinet is handed when it starts. */
export interface CabinetApi {
  /** The board as it stood when the game booted, best first. */
  board: BoardEntry[];
  /** Files a finished run. Resolves null if the board could not be reached. */
  submit(entry: BoardEntry): Promise<{ rank: number | null; entries: BoardEntry[] } | null>;
}

function toBoardEntry(entry: ScoreEntry): BoardEntry {
  return {
    name: entry.name,
    score: entry.score,
    wave: Math.max(1, Number(entry.detail?.wave ?? 1)),
  };
}

/**
 * Reads a game's board.
 *
 * Answers with an empty board rather than throwing on any failure. A cabinet
 * whose leaderboard is unreachable should still be playable — the alternative
 * is a game that will not start because a table is missing.
 */
export async function fetchBoard(gameId: string): Promise<BoardEntry[]> {
  try {
    const res = await fetch(`/api/scores/${encodeURIComponent(gameId)}?limit=10`);
    if (!res.ok) return [];
    const body = (await res.json()) as { entries?: ScoreEntry[] };
    return (body.entries ?? []).map(toBoardEntry);
  } catch {
    return [];
  }
}

/**
 * Files a score.
 *
 * Null on any failure, which the cabinet reads as "keep what you have on
 * screen" — the run still counts locally for the rest of the session, it just
 * never made it onto the wall.
 */
export async function submitScore(
  gameId: string,
  entry: BoardEntry,
): Promise<{ rank: number | null; entries: BoardEntry[] } | null> {
  try {
    const res = await fetch(`/api/scores/${encodeURIComponent(gameId)}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        name: entry.name,
        score: Math.max(0, Math.trunc(entry.score)),
        detail: { wave: Math.max(1, Math.trunc(entry.wave)) },
      }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { rank: number | null; entries?: ScoreEntry[] };
    return { rank: body.rank ?? null, entries: (body.entries ?? []).map(toBoardEntry) };
  } catch {
    return null;
  }
}
